import React from 'react';
import { Box, Card, CardContent, Typography, Grid } from '@mui/material';
import {
  TrendingDown as RiskIcon,
  TrendingUp as ConditionIcon,
  AccountBalance as BudgetIcon,
  EmojiEvents as SolutionsIcon,
} from '@mui/icons-material';
import { SolutionSummary, RunDetail } from '../../services/amsoApi';

interface KPICardsProps {
  solution: SolutionSummary | null;
  run: RunDetail;
}

function compactNumber(n: number): string {
  const abs = Math.abs(n);
  if (abs >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  if (abs >= 1_000) return `${(n / 1_000).toFixed(0)}K`;
  return n.toFixed(0);
}

interface KPICardProps {
  icon: React.ReactNode;
  label: string;
  primaryPct?: number | null;
  primaryLabel?: string;
  subtitle?: string;
  color: string;
  positiveIsGood?: boolean;
}

function KPICard({
  icon,
  label,
  primaryPct,
  primaryLabel,
  subtitle,
  color,
  positiveIsGood = false,
}: KPICardProps) {
  const hasPct = primaryPct != null && isFinite(primaryPct);
  const isGood = hasPct && (positiveIsGood ? primaryPct > 0 : primaryPct < 0);
  const pctColor = hasPct ? (isGood ? '#2e7d32' : '#c62828') : 'text.primary';

  return (
    <Card
      sx={{
        height: '100%',
        borderLeft: `3px solid ${color}`,
        transition: 'box-shadow 0.2s',
        '&:hover': { boxShadow: 4 },
      }}>
      <CardContent
        sx={{
          display: 'flex',
          alignItems: 'flex-start',
          gap: 2,
          py: 2,
          '&:last-child': { pb: 2 },
        }}>
        <Box
          sx={{
            background: `linear-gradient(135deg, ${color}22, ${color}08)`,
            color,
            borderRadius: 2,
            p: 1.2,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}>
          {icon}
        </Box>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography
            variant="overline"
            sx={{
              color: 'text.secondary',
              fontSize: '0.65rem',
              letterSpacing: '0.08em',
              lineHeight: 1.4,
            }}>
            {label}
          </Typography>
          {hasPct ? (
            <Typography
              variant="h5"
              fontWeight={700}
              noWrap
              sx={{ color: pctColor }}>
              {(primaryPct as number) < 0 ? '\u25BC' : '\u25B2'}{' '}
              {Math.abs(primaryPct as number).toFixed(1)}%
            </Typography>
          ) : (
            <Typography variant="h5" fontWeight={700} noWrap>
              {primaryLabel || '-'}
            </Typography>
          )}
          {primaryLabel && hasPct && (
            <Typography
              variant="caption"
              sx={{ color: 'text.secondary', fontSize: '0.75rem' }}
              noWrap>
              {primaryLabel}
            </Typography>
          )}
          {subtitle && (
            <Typography
              variant="caption"
              sx={{
                color: 'text.disabled',
                fontSize: '0.7rem',
                display: 'block',
              }}
              noWrap>
              {subtitle}
            </Typography>
          )}
        </Box>
      </CardContent>
    </Card>
  );
}

export default function KPICards({ solution, run }: KPICardsProps) {
  const metrics = (solution?.metrics ?? {}) as Record<string, any>;
  const groupingInfo = (run.grouping_info ?? {}) as Record<string, any>;

  const riskReductionPct: number | null = metrics.risk_reduction_pct ?? null;
  const riskPct = riskReductionPct != null ? -riskReductionPct : null;
  const baselineRisk = Number(groupingInfo.baseline_total_risk) || 0;
  const riskDelta =
    solution?.risk != null && baselineRisk > 0
      ? solution.risk - baselineRisk
      : null;
  const riskLabel =
    solution?.risk != null
      ? `${compactNumber(solution.risk)} risk score` +
        (riskDelta != null
          ? ` (${riskDelta >= 0 ? '+' : ''}${compactNumber(riskDelta)})`
          : '')
      : undefined;

  const condVsBaseline: number | null = metrics.condition_vs_baseline ?? null;
  const condPct: number | null = metrics.condition_vs_baseline_pct ?? null;
  const condLabel =
    condVsBaseline != null
      ? `${condVsBaseline >= 0 ? '+' : ''}${compactNumber(
          condVsBaseline,
        )} condition improvement`
      : solution?.condition_improvement != null
      ? `${compactNumber(solution.condition_improvement)} net change`
      : undefined;

  const budgetValue =
    solution?.budget_utilization != null
      ? `${(solution.budget_utilization * 100).toFixed(1)}%`
      : '-';
  const budgetLabel =
    metrics.budget_spent != null
      ? `$${compactNumber(Number(metrics.budget_spent))} spent`
      : undefined;

  return (
    <Grid container spacing={2}>
      <Grid item xs={12} sm={6} md={3}>
        <KPICard
          icon={<RiskIcon />}
          label="Risk Reduction"
          primaryPct={riskPct}
          primaryLabel={riskLabel}
          subtitle="vs. do-nothing baseline"
          color="#e53935"
        />
      </Grid>
      <Grid item xs={12} sm={6} md={3}>
        <KPICard
          icon={<ConditionIcon />}
          label="Condition Improvement"
          primaryPct={condPct}
          positiveIsGood
          primaryLabel={condLabel}
          subtitle="vs. do-nothing baseline"
          color="#43a047"
        />
      </Grid>
      <Grid item xs={12} sm={6} md={3}>
        <KPICard
          icon={<BudgetIcon />}
          label="Budget Utilization"
          primaryLabel={budgetValue}
          subtitle={budgetLabel}
          color="#1e88e5"
        />
      </Grid>
      <Grid item xs={12} sm={6} md={3}>
        <KPICard
          icon={<SolutionsIcon />}
          label="Solutions Found"
          primaryLabel={String(run.n_solutions ?? '-')}
          subtitle={`${run.total_assets?.toLocaleString() ?? '?'} assets`}
          color="#8e24aa"
        />
      </Grid>
    </Grid>
  );
}
